/**
 * AI Activity Tab
 * Shows AI execution history with expandable rows for full details.
 */

import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Eye,
  EyeOff,
  Cpu,
  Zap,
  AlertTriangle,
  Hash,
  Timer,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useScenarioExecutions,
  useExecutionDetail,
  useExecutionStats,
} from '../../hooks/useAIExecutions';
import type { AIExecutionLog } from '../../hooks/useAIExecutions';

interface AIActivityTabProps {
  scenarioId: string;
  onRefresh?: () => void;
}

// ============ Status Badge ============

function StatusBadge({ status }: { status: AIExecutionLog['status'] }) {
  const config = {
    started: {
      icon: Loader2,
      label: 'Running',
      className: 'bg-blue-50 text-blue-700 border-blue-200',
      animate: true,
    },
    success: {
      icon: CheckCircle2,
      label: 'Success',
      className: 'bg-green-50 text-green-700 border-green-200',
      animate: false,
    },
    failed: {
      icon: XCircle,
      label: 'Failed',
      className: 'bg-red-50 text-red-700 border-red-200',
      animate: false,
    },
    timeout: {
      icon: Clock,
      label: 'Timeout',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      animate: false,
    },
  }[status];

  const Icon = config.icon;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
        config.className,
      )}
    >
      <Icon className={clsx('w-3 h-3', config.animate && 'animate-spin')} />
      {config.label}
    </span>
  );
}

// ============ Expanded Row Detail ============

function ExecutionDetailPanel({
  scenarioId,
  logId,
}: {
  scenarioId: string;
  logId: string;
}) {
  const { data: detail, isLoading } = useExecutionDetail(scenarioId, logId);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showResponse, setShowResponse] = useState(false);

  if (isLoading) {
    return (
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading details...
        </div>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500 mb-1">Input Tokens</div>
          <div className="text-lg font-semibold text-gray-900">
            {detail.input_tokens.toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500 mb-1">Output Tokens</div>
          <div className="text-lg font-semibold text-gray-900">
            {detail.output_tokens.toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500 mb-1">Stop Reason</div>
          <div className="text-lg font-semibold text-gray-900">
            {detail.stop_reason || '-'}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500 mb-1">Request ID</div>
          <div className="text-xs font-mono text-gray-700 truncate" title={detail.request_id}>
            {detail.request_id || '-'}
          </div>
        </div>
      </div>

      {/* Prompt Section */}
      {detail.prompt_text && (
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setShowPrompt(!showPrompt)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span className="flex items-center gap-2">
              {showPrompt ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              Prompt ({detail.prompt_length.toLocaleString()} chars)
            </span>
            {showPrompt ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          {showPrompt && (
            <div className="px-4 pb-4">
              <pre className="text-xs font-mono text-gray-600 bg-gray-50 p-3 rounded border border-gray-100 max-h-96 overflow-auto whitespace-pre-wrap">
                {detail.prompt_text}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Response Section */}
      {detail.response_text && (
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setShowResponse(!showResponse)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span className="flex items-center gap-2">
              {showResponse ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              Response ({detail.response_length.toLocaleString()} chars)
            </span>
            {showResponse ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          {showResponse && (
            <div className="px-4 pb-4">
              <pre className="text-xs font-mono text-gray-600 bg-gray-50 p-3 rounded border border-gray-100 max-h-96 overflow-auto whitespace-pre-wrap">
                {detail.response_text}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Error Section */}
      {detail.error_message && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-red-800 mb-1">
            <AlertTriangle className="w-4 h-4" />
            Error ({detail.error_type})
          </div>
          <p className="text-sm text-red-700 font-mono">{detail.error_message}</p>
        </div>
      )}
    </div>
  );
}

// ============ Stats Summary ============

function StatsSummary({ scenarioId }: { scenarioId: string }) {
  const { data: stats } = useExecutionStats(scenarioId);

  if (!stats || stats.total_executions === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
          <Hash className="w-3 h-3" />
          Total Executions
        </div>
        <div className="text-xl font-semibold text-gray-900">
          {stats.total_executions}
          {stats.failed_count > 0 && (
            <span className="text-sm text-red-500 ml-1">
              ({stats.failed_count} failed)
            </span>
          )}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
          <Zap className="w-3 h-3" />
          Total Tokens
        </div>
        <div className="text-xl font-semibold text-gray-900">
          {stats.total_tokens.toLocaleString()}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
          <Cpu className="w-3 h-3" />
          Avg Tokens
        </div>
        <div className="text-xl font-semibold text-gray-900">
          {stats.avg_tokens.toLocaleString()}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
          <Timer className="w-3 h-3" />
          Avg Latency
        </div>
        <div className="text-xl font-semibold text-gray-900">
          {stats.avg_latency_seconds}s
        </div>
      </div>
    </div>
  );
}

// ============ Main Component ============

export function AIActivityTab({ scenarioId, onRefresh }: AIActivityTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [failedOnly, setFailedOnly] = useState(false);

  const filters = failedOnly ? { status: 'failed' } : undefined;
  const {
    data: executions,
    isLoading,
    refetch,
  } = useScenarioExecutions(scenarioId, filters);

  const handleRefresh = () => {
    refetch();
    onRefresh?.();
  };

  const formatTimestamp = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#45bdd6]" />
          AI Activity
        </h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={failedOnly}
              onChange={(e) => setFailedOnly(e.target.checked)}
              className="rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
            />
            Failed only
          </label>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <StatsSummary scenarioId={scenarioId} />

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading executions...
        </div>
      ) : !executions || executions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Cpu className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>No AI executions recorded yet.</p>
          <p className="text-sm mt-1">
            Trigger a scenario calculation to see activity here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[32px_1fr_100px_120px_100px_90px_140px] gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div />
            <div>Timestamp</div>
            <div>Status</div>
            <div>Type</div>
            <div>Tokens</div>
            <div>Duration</div>
            <div>Model</div>
          </div>

          {/* Table Rows */}
          {executions.map((exec) => (
            <div key={exec.id}>
              <button
                onClick={() =>
                  setExpandedId(expandedId === exec.id ? null : exec.id)
                }
                className={clsx(
                  'w-full grid grid-cols-[32px_1fr_100px_120px_100px_90px_140px] gap-2 px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors border-b border-gray-100',
                  expandedId === exec.id && 'bg-gray-50',
                )}
              >
                <div className="flex items-center">
                  {expandedId === exec.id ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <div className="text-gray-900 font-mono text-xs">
                  {formatTimestamp(exec.created_at)}
                </div>
                <div>
                  <StatusBadge status={exec.status} />
                </div>
                <div className="text-gray-600">{exec.execution_type_display}</div>
                <div className="text-gray-900 font-medium">
                  {exec.tokens_display}
                </div>
                <div className="text-gray-600">{exec.duration_display}</div>
                <div className="text-gray-500 text-xs truncate" title={exec.model}>
                  {exec.model || '-'}
                </div>
              </button>

              {/* Expanded Detail */}
              {expandedId === exec.id && (
                <ExecutionDetailPanel
                  scenarioId={scenarioId}
                  logId={exec.id}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
